'use client'

import { useEffect } from "react";
import { useGoogleIdentity, GOOGLE_CLIENT_ID } from "../../lib/google/gapi";
import { isRunningInBrowser } from "../../lib/browser";
import { createClient } from "../../lib/supabase/client";
import { toast } from '@/components/ui';
import { useRouter } from 'next/navigation';
import { identifyUser } from '../../lib/tracking';

export function SigninWithGoogleButton({
  className = '',
  onClick,
  id,
  size = 'large',
  redirectTo = null
}: {
  className?: string,
  size?: 'large' | 'medium' | 'small',
  onClick?: () => void,
  id: string,
  redirectTo?: string | null
}) {
  const { googleIdentity } = useGoogleIdentity()
  const router = useRouter()
  
  useEffect(() => {
    if (Boolean(googleIdentity) && isRunningInBrowser()) {
      const parent = document.getElementById(id)
      if (!parent) return;
      
      googleIdentity!.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        log_level: 'debug',
        auto_select: true,
        ux_mode: 'popup',
        itp_support: true,

        callback: (args: any) => {
          console.log({ CBArgs: args })
          handleSignInWithGoogle(args)
            .then(success => {
              if (success) {
                toast.success('Successfully signed in with Google!');
                
                // Redirect to the specified path after successful sign-in if redirectTo is provided
                if (redirectTo) {
                  setTimeout(() => {
                    router.push(redirectTo);
                  }, 500); // Small delay to allow the toast to be seen
                }
              }
              
              if (onClick) {
                onClick()
              }
            })
            .catch(error => {
              console.error('Google sign-in error:', error);
              toast.error('Failed to sign in with Google. Please try again later.');
            });
        }
      });
      
      googleIdentity!.accounts.id.renderButton(parent!, {
        theme: "filled_blue",
        text: 'continue_with',
        size
      })
      
      googleIdentity!.accounts.id.prompt((notification: any) => {
        console.log({
          isDisplayMoment: notification.isDisplayMoment(),
          isDisplayed: notification.isDisplayed(),
          isNotDisplayed: notification.isNotDisplayed(),
          getNotDisplayedReason: notification.getNotDisplayedReason(),
          isSkippedMoment: notification.isSkippedMoment(),
          getSkippedReason: notification.getSkippedReason(),
          isDismissedMoment: notification.isDismissedMoment(),
          getDismissedReason: notification.getDismissedReason(),
          getMomentType: notification.getMomentType()
        })
      })
    }
  }, [googleIdentity, id, onClick, size, router, redirectTo])

  return (
    <div className={`${className} max-w-full overflow-hidden my-auto`}>
      <div id={id} />
    </div>
  )
}

async function handleSignInWithGoogle(response: any): Promise<boolean> {
  try {
    const supabase = createClient()
    
    // Use the Supabase client directly to sign in with the Google ID token
    const { data, error } = await supabase.auth.signInWithIdToken({
      provider: 'google',
      token: response.credential,
      nonce: '',
    })
    
    if (error) {
      console.error('Supabase auth error:', error);
      toast.error(error.message || 'Authentication failed');
      return false;
    }
    
    console.log({ data });
    return true;
    
  } catch (error) {
    console.error('Error in handleSignInWithGoogle:', error);
    toast.error('Failed to sign in with Google. Please try again later.');
    throw error; // Re-throw to be caught by the caller
  }
} 